/**
 * "Edit address" dialog for a Shopify (clensta.com) website order — used by
 * ShopifyOrderList.js and ShopifyOrderDetails.js.
 *
 * Loads the order fresh (GET /shopify-orders/:id, which also returns the state
 * list), then saves via POST /shopify-orders/:id/address. Allowed until the
 * order is booked on ShipShopy. Before the Unicommerce invoice exists the edit
 * is "full" (Unicommerce + Shopify + the address we send ShipShopy); after it,
 * "shipping_only" (Shopify + ShipShopy; the invoice keeps the old address).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";

const API = `${process.env.REACT_APP_AI_PATH}/api/ai/shopify-orders`;

export default function ShopifyAddressDialog({ orderId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState("");
  const [mode, setMode] = useState("full");
  const [orderName, setOrderName] = useState("");
  const [states, setStates] = useState({});
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", address1: "", address2: "", city: "", stateCode: "", pincode: "" });

  useEffect(() => {
    axios
      .get(`${API}/${orderId}`)
      .then((res) => {
        if (!res.data?.success) { setError(res.data?.message || "Could not load the order"); return; }
        const o = res.data.order;
        const st = res.data.states || {};
        const a = o.shippingAddress || {};
        const [firstName, ...rest] = String(a.name || o.customer?.name || "").trim().split(/\s+/);
        // Older rows have only the state name — map it back to its code.
        const stateCode = a.stateCode || Object.keys(st).find((k) => st[k].toLowerCase() === String(a.state || "").toLowerCase()) || "";
        setStates(st);
        setOrderName(o.orderName);
        setBlocked(o.canEditAddress ? "" : (o.editAddressBlockedReason || "This order's address can no longer be changed"));
        setMode(o.editAddressMode || "full");
        setForm({
          firstName: firstName || "", lastName: rest.join(" "), phone: a.phone || o.customer?.phone || "",
          address1: a.address1 || "", address2: a.address2 || "", city: a.city || "", stateCode, pincode: a.pincode || "",
        });
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [orderId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = () => {
    setSaving(true);
    setError("");
    axios
      .post(`${API}/${orderId}/address`, form)
      .then((res) => {
        if (res.data?.order && onSaved) onSaved(res.data.order);
        if (res.data?.success) {
          alert(res.data.message || "Address updated");
          onClose();
        } else {
          setError(res.data?.message || "Could not update the address");
        }
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setSaving(false));
  };

  const disabled = !!blocked || saving;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit buyer name & address{orderName ? ` — ${orderName}` : ""}
        <Typography variant="caption" display="block" color="textSecondary">
          Allowed only until the order is pushed to ShipShopy.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box textAlign="center" p={3}><CircularProgress size={28} /></Box>}
        {!loading && !blocked && mode === "full" && (
          <Box mb={2} p={1.5} style={{ background: "#e8f5e9", borderRadius: 6 }}>
            <Typography variant="body2" style={{ color: "#2e7d32" }}>
              Invoice not generated yet — this updates Unicommerce (billing + shipping, so the invoice will show it), Shopify, and the address sent to ShipShopy.
            </Typography>
          </Box>
        )}
        {!loading && !blocked && mode === "shipping_only" && (
          <Box mb={2} p={1.5} style={{ background: "#fff4e5", borderRadius: 6 }}>
            <Typography variant="body2" style={{ color: "#b26a00" }}>
              Invoice is already generated in Unicommerce. This updates the address sent to ShipShopy (the courier) and Shopify only — the Unicommerce order and invoice keep the old name/address.
            </Typography>
          </Box>
        )}
        {!loading && blocked && (
          <Box mb={2} p={1.5} style={{ background: "#fff4e5", borderRadius: 6 }}>
            <Typography variant="body2" style={{ color: "#b26a00" }}>{blocked}</Typography>
          </Box>
        )}
        {!loading && (
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="First name *" value={form.firstName} onChange={set("firstName")} disabled={disabled} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Last name" value={form.lastName} onChange={set("lastName")} disabled={disabled} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Mobile (10 digits) *" value={form.phone} onChange={set("phone")} disabled={disabled} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Address line 1 *" value={form.address1} onChange={set("address1")} disabled={disabled} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Address line 2" value={form.address2} onChange={set("address2")} disabled={disabled} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="City *" value={form.city} onChange={set("city")} disabled={disabled} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Pincode *" value={form.pincode} onChange={set("pincode")} disabled={disabled} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="State *" value={form.stateCode} onChange={set("stateCode")} disabled={disabled}>
                {Object.entries(states).sort((x, y) => x[1].localeCompare(y[1])).map(([code, name]) => (
                  <MenuItem key={code} value={code}>{name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}
        {error && <Typography color="error" variant="body2" style={{ marginTop: 12 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <LoadingButton variant="contained" loading={saving} disabled={loading || !!blocked} onClick={save}>
          {mode === "shipping_only" ? "Save for courier & Shopify" : "Save on Unicommerce, Shopify & courier"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
